import type * as React from "react";
import { useMemo, useState } from "react";
import { Search, X, type LucideIcon } from "lucide-react";
import {
  actionBarButtonActiveClassName,
  actionBarButtonClassName,
  actionBarIconClassName,
} from "@/components/ui/action-bar";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface ActionBarSelectOption {
  value: string;
  label: string;
  // Secondary line beneath the label — a phone number under its display name,
  // say. Also matched by the search, so a user can type either part.
  hint?: string;
}

export interface ActionBarSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly ActionBarSelectOption[];
  icon?: LucideIcon;
  // Accessible name for the trigger — the visible text is the selection.
  ariaLabel: string;
  // Shown on the trigger while nothing is applied — the field's name ("Phone
  // number"), not the reset option's label. Defaults to `ariaLabel`.
  placeholder?: string;
  // The value that means "no filter". Applying anything else turns the trigger
  // into a removable pill: it reads the selection and gains an × that clears back
  // to this value. Omit for a pill that is always set (a period, say).
  clearValue?: string;
  // Adds a search field above the list. Worth it past a handful of options; a
  // two-option pill (Active / Archived) reads better without one.
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  // Accessible name for the × — defaults to `Clear <ariaLabel>`.
  clearLabel?: string;
  contentClassName?: string;
}

// Single-select action-bar pill: the trigger reads as the current selection and
// carries no chevron, matching the Date range and All agents pills. Picking an
// option applies it immediately and closes the menu — unlike the column toggle,
// there is nothing to multi-select.
//
// Two optional variants, both driven by the data rather than a mode flag:
// `searchable` for long lists, and a per-option `hint` for a two-line row.
export function ActionBarSelect({
  value,
  onChange,
  options,
  icon: Icon,
  ariaLabel,
  placeholder,
  clearValue,
  searchable = false,
  searchPlaceholder = "Search…",
  emptyMessage = "No matches",
  clearLabel,
  contentClassName,
}: ActionBarSelectProps): React.ReactElement {
  const [query, setQuery] = useState("");
  const selected = options.find((option) => option.value === value);
  const isApplied = clearValue === undefined ? true : value !== clearValue;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.hint?.toLowerCase().includes(needle)
    );
  }, [options, query]);

  return (
    <DropdownMenu
      // Reset the filter between visits so reopening never shows a stale subset.
      onOpenChange={(open) => !open && setQuery("")}
    >
      {/* The box lives on the wrapper so the clear sits beside the trigger: a
          focusable control inside a <button> is interactive content in a button,
          which the HTML content model forbids. */}
      <div
        data-slot="action-bar-button"
        data-active={isApplied || undefined}
        className={cn(
          actionBarButtonClassName,
          isApplied && actionBarButtonActiveClassName
        )}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            // `outline-none` with nothing put back in its place: tabbing to this
            // pill removed the browser's focus ring and drew nothing, so keyboard
            // focus simply vanished for one control in the middle of the toolbar.
            // The ring is scoped to `focus-visible`, so a mouse click still does
            // not draw one.
            className={cn(
              "inline-flex cursor-pointer items-center gap-2 rounded-md bg-transparent focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
              actionBarIconClassName
            )}
          >
            {Icon ? <Icon className="size-4" /> : null}
            <span className="truncate">
              {isApplied
                ? (selected?.label ?? value)
                : (placeholder ?? ariaLabel)}
            </span>
          </button>
        </DropdownMenuTrigger>
        {isApplied && clearValue !== undefined && (
          // A sibling button, not a click handler on the pill: clearing must not
          // also open the menu.
          <button
            type="button"
            aria-label={clearLabel ?? `Clear ${ariaLabel}`}
            className="hover:text-foreground text-muted-foreground -mr-1 ml-0.5 inline-flex cursor-pointer items-center rounded p-0.5"
            onPointerDown={(event) => {
              // Radix opens the menu on pointerdown; stop it there so the clear
              // itself can ride the native click, which keyboard and assistive
              // tech also emit.
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={() => onChange(clearValue)}
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <DropdownMenuContent
        align="start"
        className={cn("w-64 overflow-hidden p-0", contentClassName)}
      >
        {searchable && (
          <div className="relative border-b p-2">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="h-8 pl-8"
              // Radix treats printable typing as its own typeahead, which would
              // fight the field — but its Escape/arrow/Home/End handling is the
              // only way a keyboard user leaves this input, so those must
              // propagate.
              onKeyDown={(event) => {
                if (event.key.length === 1) event.stopPropagation();
              }}
            />
          </div>
        )}

        <div className="max-h-72 overflow-y-auto px-2 py-2">
          {visible.length === 0 ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">
              {emptyMessage}
            </p>
          ) : (
            visible.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={option.value === value}
                onCheckedChange={() => onChange(option.value)}
                // `min-h-8` rather than a fixed height: a long label wraps, and a
                // fixed row would let it overflow onto the next option. `pr-8`
                // keeps the text clear of the check.
                className={cn(
                  "min-h-8 items-start rounded-md py-1.5 pr-8 pl-3",
                  // Check on the right, matching the column toggle and the date
                  // range's "Custom" row.
                  "[&>span:first-child]:top-2 [&>span:first-child]:right-3 [&>span:first-child]:left-auto [&>span:first-child]:translate-y-0 [&>span:first-child]:text-info"
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block">{option.label}</span>
                  {option.hint && (
                    <span className="text-muted-foreground block text-xs">
                      {option.hint}
                    </span>
                  )}
                </span>
              </DropdownMenuCheckboxItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
